import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create a dedicated axios instance for shop (public routes)
const shopApi = axios.create({
  baseURL: `${API_URL}/api/v1/shop`, // Base URL for shop routes
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Optional: Add request logging in development
shopApi.interceptors.request.use(
  (config) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`📤 [ShopService] ${config.method.toUpperCase()} ${config.url}`, config.params || config.data);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Optional: Add response logging in development
shopApi.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`📥 [ShopService] ${response.config.method.toUpperCase()} ${response.config.url} - ${response.status}`, response.data);
    }
    return response;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(`💥 [ShopService] Error:`, error.response?.data || error.message);
    }
    return Promise.reject(error);
  }
);

export const shopService = {
  // ==================== PRODUCT LISTINGS ====================
  /**
   * Get all products with pagination, filtering, and sorting
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.pageSize - Items per page (default: 12)
   * @param {string} params.type - Filter by product type
   * @param {string} params.search - Search query
   * @param {string} params.sort - Sort option (price-asc, price-desc, newest, oldest, name-asc, name-desc)
   * @returns {Promise<Object>} Products data with pagination
   */
  async getProducts(params = {}) {
    console.log('📤 [ShopService] Fetching products with params:', params);
    try {
      const queryParams = new URLSearchParams();
      
      // Add all params to query string
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      
      const response = await shopApi.get(`/?${queryParams.toString()}`);
      console.log('📥 [ShopService] Products fetched successfully');
      return response.data;
    } catch (error) {
      console.error('💥 [ShopService] Get products error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to fetch products');
    }
  },

  // ==================== SINGLE PRODUCT ====================
  /**
   * Get single product by slug
   * @param {string} slug - Product slug
   * @returns {Promise<Object>} Product data with related products
   */
  async getProductBySlug(slug) {
    console.log('📤 [ShopService] Fetching product by slug:', slug);
    
    // Validate slug
    if (!slug || typeof slug !== 'string') {
      throw new Error('Invalid product slug');
    }
    
    try {
      const response = await shopApi.get(`/${slug}`);
      console.log('📥 [ShopService] Product fetched successfully:', response.data.data?.name);
      return response.data;
    } catch (error) {
      console.error('💥 [ShopService] Get product error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to fetch product');
    }
  },

  // ==================== FEATURED PRODUCTS ====================
  /**
   * Get featured products
   * @returns {Promise<Object>} Featured products data
   */
  async getFeaturedProducts() {
    console.log('📤 [ShopService] Fetching featured products...');
    try {
      const response = await shopApi.get('/featured/featured');
      console.log('📥 [ShopService] Featured products fetched:', response.data.data?.length || 0, 'items');
      return response.data;
    } catch (error) {
      console.error('💥 [ShopService] Get featured products error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to fetch featured products');
    }
  },

  // ==================== PRODUCTS BY TYPE ====================
  /**
   * Get products by type
   * @param {string} type - Product type (standalone/collection)
   * @returns {Promise<Object>} Products data
   */
  async getProductsByType(type) {
    console.log('📤 [ShopService] Fetching products by type:', type);
    
    // Validate type
    if (!type || !['standalone', 'collection'].includes(type)) {
      throw new Error('Invalid product type. Must be "standalone" or "collection"');
    }
    
    try {
      const response = await shopApi.get(`/type/${type}`);
      console.log('📥 [ShopService] Products by type fetched:', response.data.data?.length || 0, 'items');
      return response.data;
    } catch (error) {
      console.error('💥 [ShopService] Get products by type error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to fetch products by type');
    }
  },

  // ==================== SEARCH ====================
  /**
   * Search products
   * @param {string} query - Search query
   * @param {number} limit - Number of results to return (default: 10)
   * @returns {Promise<Object>} Search results
   */
  async searchProducts(query, limit = 10) {
    console.log('📤 [ShopService] Searching products with query:', query);
    
    // Validate query
    if (!query || typeof query !== 'string' || query.trim().length < 2) {
      throw new Error('Search query must be at least 2 characters');
    }
    
    try {
      const response = await shopApi.get(`/search?q=${encodeURIComponent(query.trim())}&limit=${limit}`);
      console.log('📥 [ShopService] Search results:', response.data.data?.length || 0, 'items found');
      return response.data;
    } catch (error) {
      console.error('💥 [ShopService] Search products error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to search products');
    }
  },

  // ==================== NEW METHODS TO ADD ====================

  /**
   * Get product categories/types available
   * @returns {Promise<Array>} List of available product types
   */
  async getProductTypes() {
    console.log('📤 [ShopService] Fetching product types...');
    try {
      // This would need a new endpoint or reuse getProducts with limit 1 to get filters
      const response = await this.getProducts({ pageSize: 1 });
      return response.filters?.types || [];
    } catch (error) {
      console.error('💥 [ShopService] Get product types error:', error);
      return [];
    }
  },

  /**
   * Get price range for products
   * @returns {Promise<Object>} Min and max price
   */
  async getPriceRange() {
    console.log('📤 [ShopService] Fetching price range...');
    try {
      const response = await this.getProducts({ pageSize: 1 });
      return response.filters?.priceRange || { minPrice: 0, maxPrice: 0 };
    } catch (error) {
      console.error('💥 [ShopService] Get price range error:', error);
      return { minPrice: 0, maxPrice: 0 };
    }
  },

  /**
   * Get related products for a specific product
   * @param {string} productId - Product ID
   * @param {number} limit - Number of related products (default: 4)
   * @returns {Promise<Array>} Related products
   */
  async getRelatedProducts(productId, limit = 4) {
    console.log('📤 [ShopService] Fetching related products for:', productId);
    
    if (!this.isValidObjectId(productId)) {
      throw new Error('Invalid product ID');
    }
    
    try {
      // This would need a new endpoint or you can use the related products from getProductBySlug
      const product = await this.getProductBySlug(productId);
      return product.data?.relatedProducts || [];
    } catch (error) {
      console.error('💥 [ShopService] Get related products error:', error);
      return [];
    }
  },

  // ==================== UTILITIES ====================

  /**
   * Format price to Nigerian Naira
   * @param {number} price - Price to format
   * @returns {string} Formatted price
   */
  formatPrice(price) {
    if (price === undefined || price === null) return 'Price on request';
    return `₦${parseFloat(price).toLocaleString()}`;
  },

  /**
   * Format date
   * @param {string|Date} date - Date to format
   * @returns {string} Formatted date
   */
  formatDate(date) {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  /**
   * Validate MongoDB ObjectId
   * @param {string} id - ID to validate
   * @returns {boolean} Whether ID is valid
   */
  isValidObjectId(id) {
    return /^[0-9a-fA-F]{24}$/.test(id);
  },

  /**
   * Get product status badge color
   * @param {string} status - Product status
   * @returns {string} Tailwind CSS color class
   */
  getStatusBadgeColor(status) {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'inactive': 'bg-gray-100 text-gray-800',
      'out-of-stock': 'bg-red-100 text-red-800',
      'low-stock': 'bg-yellow-100 text-yellow-800',
      'draft': 'bg-gray-100 text-gray-800',
      'archived': 'bg-gray-100 text-gray-600'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  },

  /**
   * Get inventory status text
   * @param {Object} product - Product object
   * @returns {string} Inventory status text
   */
  getInventoryStatus(product) {
    if (!product) return 'Unknown';
    
    if (product.stockQuantity <= 0) return 'Out of Stock';
    if (product.stockQuantity < 10) return `Low Stock (${product.stockQuantity} left)`;
    if (product.stockQuantity < 50) return `In Stock (${product.stockQuantity} available)`;
    return 'In Stock';
  },

  /**
   * Get product URL
   * @param {Object} product - Product object
   * @returns {string} Product URL
   */
  getProductUrl(product) {
    if (!product) return '#';
    return `/product/${product.slug || product._id}`;
  },

  /**
   * Get primary product image
   * @param {Object} product - Product object
   * @returns {string} Image URL
   */
  getPrimaryImage(product) {
    if (!product?.images || product.images.length === 0) {
      return '/images/placeholder-product.jpg'; // Add a placeholder
    }
    
    const primary = product.images.find(img => img.isPrimary);
    return primary?.url || product.images[0]?.url || '/images/placeholder-product.jpg';
  },

  /**
   * Build query string for filtering
   * @param {Object} filters - Filter object
   * @returns {string} Query string
   */
  buildQueryString(filters) {
    const params = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        if (Array.isArray(filters[key])) {
          filters[key].forEach(value => params.append(key, value));
        } else {
          params.append(key, filters[key]);
        }
      }
    });
    
    return params.toString();
  },

  /**
   * Clear product cache (if you implement caching)
   */
  clearCache() {
    console.log('🧹 [ShopService] Clearing cache...');
    // Implement if you add caching
  },

  // ==================== ERROR HANDLING ====================

  /**
   * Handle API errors gracefully
   * @param {Error} error - Error object
   * @returns {Object} Formatted error
   */
  handleError(error) {
    const defaultMessage = 'An unexpected error occurred';
    
    if (error.response) {
      // Server responded with error
      return {
        message: error.response.data?.message || defaultMessage,
        status: error.response.status,
        data: error.response.data
      };
    } else if (error.request) {
      // Request made but no response
      return {
        message: 'Network error. Please check your connection.',
        status: 0,
        data: null
      };
    } else {
      // Something else happened
      return {
        message: error.message || defaultMessage,
        status: 500,
        data: null
      };
    }
  }
};

// Export individual functions for direct import
export const {
  getProducts,
  getProductBySlug,
  getFeaturedProducts,
  getProductsByType,
  searchProducts,
  getProductTypes,
  getPriceRange,
  getRelatedProducts,
  formatPrice,
  formatDate,
  isValidObjectId,
  getStatusBadgeColor,
  getInventoryStatus,
  getProductUrl,
  getPrimaryImage,
  buildQueryString,
  handleError
} = shopService;

// Also export the service as default
export default shopService;